import React, { useState, useCallback } from 'react';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';
import { useGameBalance } from './hooks/useGameBalance';
import { useMultiplier } from './hooks/useMultiplier';
import { useSounds } from './hooks/useSounds';
import { formatLilape } from './config/gameConfig';
import { DepositWithdrawPanel } from './components/DepositWithdrawPanel';

const GRID = 25;
const MINE_OPTIONS = [3, 5, 10, 15];
const REVEAL_OPTIONS = [1, 3, 5, 8];
const BET_OPTIONS = [20_000_000, 50_000_000, 100_000_000, 500_000_000];
const MAX_MULTIPLIER = 20; // Cap maximum win at 20x
const HOUSE_EDGE = 0.92; // 8% house edge per reveal

function calcMultiplier(mines, reveals) {
  let mult = 1;
  for (let i = 0; i < reveals; i++) {
    const safeLeft = GRID - mines - i;
    const totalLeft = GRID - i;
    mult *= (totalLeft / safeLeft) * HOUSE_EDGE;
  }
  // Cap at max multiplier
  return Math.min(Math.round(mult * 100) / 100, MAX_MULTIPLIER);
}

function placeMinesLocal(seed, mineCount) {
  let s = seed;
  const rand = () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 0xffffffff; };
  const cells = Array.from({ length: GRID }, (_, i) => i);
  const mines = new Set();
  for (let i = 0; i < mineCount; i++) {
    const j = i + Math.floor(rand() * (GRID - i));
    [cells[i], cells[j]] = [cells[j], cells[i]];
    mines.add(cells[i]);
  }
  return mines;
}

const WIN_RATE = 0.03; // 3% win rate

function getRevealSeq(mines, revealCount) {
  // 97% chance to hit a mine (only 3% win rate)
  const hitMine = Math.random() > WIN_RATE;

  if (hitMine) {
    // Reveal some safe cells then hit a mine
    const safe = [];
    const mineArr = [...mines];
    for (let i = 0; i < GRID && safe.length < revealCount - 1; i++) {
      if (!mines.has(i)) safe.push(i);
    }
    // Add a random mine at the end to lose
    const randomMine = mineArr[Math.floor(Math.random() * mineArr.length)];
    return [...safe.slice(0, Math.min(safe.length, revealCount - 1)), randomMine];
  }

  // 10% win - reveal only safe cells
  const safe = [];
  for (let i = 0; i < GRID && safe.length < revealCount; i++) {
    if (!mines.has(i)) safe.push(i);
  }
  return safe;
}

export function MinesGame({ onBack }) {
  const { hapticFeedback } = useTelegram();
  const { connected, sendTransaction, address } = useTon();
  const gameBalanceHook = useGameBalance(address, sendTransaction);
  const { gameBalance, useSpin, addWinnings, canSpin } = gameBalanceHook;
  const { applyMultiplier } = useMultiplier();
  const { play: playSound, playTone } = useSounds();

  const [bet, setBet] = useState(BET_OPTIONS[0]);
  const [mines, setMines] = useState(MINE_OPTIONS[0]);
  const [reveals, setReveals] = useState(REVEAL_OPTIONS[1]);
  const [playing, setPlaying] = useState(false);
  const [grid, setGrid] = useState(Array(GRID).fill('hidden'));
  const [outcome, setOutcome] = useState(null);
  const [winAmt, setWinAmt] = useState(0);
  const [minePos, setMinePos] = useState(new Set());
  const [showWallet, setShowWallet] = useState(false);

  const maxReveals = GRID - mines;
  const validReveals = REVEAL_OPTIONS.filter(r => r <= maxReveals);
  const mult = calcMultiplier(mines, reveals);

  const animateReveal = useCallback((revealSeq, mineSet, onDone) => {
    let i = 0;
    const step = () => {
      if (i < revealSeq.length) {
        const idx = revealSeq[i];
        const isMine = mineSet.has(idx);
        setGrid(g => { const n = [...g]; n[idx] = isMine ? 'exploded' : 'safe'; return n; });

        if (isMine) {
          // Hit a mine - show all mines and stop
          playTone(200, 0.2, 'sawtooth');
          hapticFeedback?.('error');
          setTimeout(() => {
            setGrid(g => {
              const n = [...g];
              mineSet.forEach(m => { if (n[m] === 'hidden') n[m] = 'mine'; });
              return n;
            });
            onDone();
          }, 300);
          return;
        }

        playTone(600 + i * 80, 0.05, 'sine');
        hapticFeedback?.('light');
        i++;
        setTimeout(step, 150);
      } else {
        setGrid(g => {
          const n = [...g];
          mineSet.forEach(m => { if (n[m] === 'hidden') n[m] = 'mine'; });
          return n;
        });
        onDone();
      }
    };
    setTimeout(step, 200);
  }, [hapticFeedback, playTone]);

  const playGame = useCallback(async () => {
    if (playing) return;
    if (!canSpin(bet)) {
      hapticFeedback?.('error');
      return;
    }
    const betSuccess = await useSpin(bet, 'mines');
    if (!betSuccess) return;

    hapticFeedback?.('medium');
    setPlaying(true);
    setOutcome(null);
    setGrid(Array(GRID).fill('hidden'));
    setMinePos(new Set());

    const seed = Math.floor(Math.random() * 0x7fffffff);
    const mineSet = placeMinesLocal(seed, mines);
    const revealSeq = getRevealSeq(mineSet, reveals);
    setMinePos(mineSet);

    // Check if any revealed cell is a mine
    const hitMine = revealSeq.some(idx => mineSet.has(idx));

    animateReveal(revealSeq, mineSet, () => {
      if (hitMine) {
        // Player lost - hit a mine
        setWinAmt(0);
        setOutcome('lose');
        playSound('lose');
        setPlaying(false);
        hapticFeedback?.('error');
      } else {
        // Player won - all safe
        const baseGain = Math.round(bet * mult);
        const gain = applyMultiplier(baseGain);
        addWinnings(gain, 'mines', bet);
        setWinAmt(gain);
        setOutcome('win');
        playSound(mult >= 2 ? 'bigWin' : 'win');
        setPlaying(false);
        hapticFeedback?.('heavy');
      }
    });
  }, [playing, bet, mines, reveals, mult, canSpin, useSpin, addWinnings, applyMultiplier, hapticFeedback, animateReveal, playSound]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
      padding: '16px',
      paddingBottom: '100px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: '12px',
          padding: '10px 16px',
          color: '#fff',
          fontSize: '16px',
          cursor: 'pointer',
        }}>← Back</button>
        <h1 style={{
          flex: 1,
          textAlign: 'center',
          color: '#EF4444',
          fontSize: '24px',
          margin: 0,
        }}>💣 Mines</h1>
      </div>

      {/* Balance */}
      <div style={{
        background: 'rgba(239,68,68,0.1)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: '16px',
        padding: '16px',
        textAlign: 'center',
        marginBottom: '20px',
      }}>
        <div style={{ color: '#888', fontSize: '12px' }}>GAME BALANCE</div>
        <div style={{ color: '#EF4444', fontSize: '28px', fontWeight: 'bold', marginBottom: 8 }}>
          {formatLilape(gameBalance)} LILAPE
        </div>
        <button onClick={() => setShowWallet(true)} style={{
          padding: '8px 20px', borderRadius: 8,
          background: 'linear-gradient(135deg, #10B981, #059669)',
          border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>💰 Deposit / Withdraw</button>
      </div>

      {showWallet && <DepositWithdrawPanel onClose={() => setShowWallet(false)} gameBalanceHook={gameBalanceHook} />}

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '6px',
        marginBottom: '20px',
        maxWidth: '280px',
        margin: '0 auto 20px',
      }}>
        {grid.map((cell, i) => (
          <div key={i} style={{
            aspectRatio: '1',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            background: cell === 'safe' ? 'rgba(16,185,129,0.3)'
              : cell === 'mine' || cell === 'exploded' ? 'rgba(239,68,68,0.3)'
                : 'rgba(255,255,255,0.08)',
            border: `1px solid ${cell === 'safe' ? 'rgba(16,185,129,0.5)'
              : cell === 'mine' || cell === 'exploded' ? 'rgba(239,68,68,0.5)'
                : 'rgba(255,255,255,0.15)'}`,
            transition: 'all 0.2s',
            animation: cell === 'exploded' ? 'shake 0.3s' : 'none',
          }}>
            {cell === 'safe' ? '💎' : cell === 'mine' ? '💣' : cell === 'exploded' ? '💥' : ''}
          </div>
        ))}
      </div>

      {/* Result */}
      {outcome && (
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          padding: '16px',
          borderRadius: '12px',
          background: outcome === 'win' ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)',
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: outcome === 'win' ? '#00ff00' : '#ff4444',
          }}>
            {outcome === 'win' ? `🎉 WIN +${formatLilape(winAmt)}` : '💥 BOOM! You hit a mine!'}
          </div>
          <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
            {outcome === 'win' ? `Multiplier: ${mult}×` : `Lost: ${formatLilape(bet)}`}
          </div>
        </div>
      )}

      {/* Mine Count */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          NUMBER OF MINES
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          {MINE_OPTIONS.map(m => (
            <button
              key={m}
              onClick={() => {
                if (!playing) {
                  setMines(m);
                  const newMax = GRID - m;
                  if (reveals > newMax) setReveals(Math.max(1, newMax));
                }
              }}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: mines === m ? '2px solid #EF4444' : '1px solid rgba(255,255,255,0.2)',
                background: mines === m ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)',
                color: mines === m ? '#EF4444' : '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                opacity: playing ? 0.5 : 1,
              }}
            >{m}</button>
          ))}
        </div>
      </div>

      {/* Reveal Count */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          GEMS TO REVEAL ({mult}× payout)
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {validReveals.map(r => (
            <button
              key={r}
              onClick={() => !playing && setReveals(r)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: reveals === r ? '2px solid #10B981' : '1px solid rgba(255,255,255,0.2)',
                background: reveals === r ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
                color: reveals === r ? '#10B981' : '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                opacity: playing ? 0.5 : 1,
              }}
            >{r}</button>
          ))}
        </div>
      </div>

      {/* Bet Options */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          BET AMOUNT
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {BET_OPTIONS.map(opt => (
            <button
              key={opt}
              onClick={() => !playing && setBet(opt)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: bet === opt ? '2px solid #EF4444' : '1px solid rgba(255,255,255,0.2)',
                background: bet === opt ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)',
                color: bet === opt ? '#EF4444' : '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                opacity: playing ? 0.5 : 1,
              }}
            >{formatLilape(opt)}</button>
          ))}
        </div>
      </div>

      {/* Play Button */}
      <button
        onClick={playGame}
        disabled={playing || !canSpin(bet)}
        style={{
          width: '100%',
          padding: '18px',
          borderRadius: '14px',
          border: 'none',
          background: playing ? '#555' : canSpin(bet) ? 'linear-gradient(135deg, #EF4444, #DC2626)' : '#333',
          color: '#fff',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: playing || !canSpin(bet) ? 'not-allowed' : 'pointer',
        }}
      >
        {playing ? '💎 REVEALING...' : `PLAY ${formatLilape(bet)}`}
      </button>
    </div>
  );
}
